import React, { useState } from "react";
import { Link } from "wouter";
import { MailCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "./ui/button";

interface NewsletterResponse {
  ok?: boolean;
  error?: string;
  message?: string;
  detail?: string;
}

interface NewsletterPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  language: string;
  source: string;
  tags: string[];
}

export function Newsletter() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExistingSubscriber, setIsExistingSubscriber] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !email.trim()) return;

    setLoading(true);
    setError(null);
    setSubmitted(false);
    setIsExistingSubscriber(false);

    const payload: NewsletterPayload = {
      email: email.trim(),
      language: i18n.language,
      source: 'website-newsletter',
      tags: ['website', 'newsletter'],
    };

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL;

      const response = await axios.post<NewsletterResponse>(apiUrl + '/newsletter', payload, {
        headers: { 'Content-Type': 'application/json' },
        validateStatus: () => true, // Handle all statuses
      });

      console.log('Newsletter API response:', response.status, response.data);

      // Success: new subscriber
      if (response.status >= 200 && response.status < 300) {
        setSubmitted(true);
        setIsExistingSubscriber(false);
        return;
      }

      // Already subscribed
      if (response.data?.error === 'already_subscribed' || response.status === 409) {
        setSubmitted(true);
        setIsExistingSubscriber(true);
        return;
      }

      // Handle known error codes
      const errorCode = response.data?.error;
      if (errorCode === 'invalid_email') {
        setError(t('newsletter.invalidEmail') ?? 'Please enter a valid email.');
      } else if (errorCode === 'mailchimp_not_configured') {
        setError('Newsletter temporarily unavailable. Please try again later.');
      } else {
        setError(t('newsletter.errorGeneric') ?? 'Subscription failed. Please try again later.');
      }
    } catch (err) {
      console.error('Network or unexpected error:', err);
      setError(t('newsletter.errorGeneric') ?? 'Subscription failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setEmail("");
    setError(null);
    setIsExistingSubscriber(false);
  };

  return (
    <section className="max-w-screen-xl mx-auto bg-transparent py-10 px-0 flex flex-col md:flex-row items-center justify-between mb-10 transition-colors relative overflow-hidden">
      <div className="mb-8 md:mb-0 md:w-2/5 z-10 px-6 md:px-0">
        <h2 className="font-extrabold text-xl md:text-xl mb-2 text-gray-900 dark:text-white flex items-center gap-2 tracking-tight">
          <MailCheck className="h-7 w-7 text-slate-900 dark:text-white inline-block mr-4" />
          {t("newsletter.title")}
        </h2>
        <p className="text-base text-gray-700 dark:text-gray-300 mb-3">{t("newsletter.description")}</p>
      </div>

      <div className="md:w-3/5 w-full z-10 flex flex-col items-end px-6 md:px-0">
        {!submitted ? (
          <>
            <form
              className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto justify-end"
              onSubmit={handleSubmit}
            >
              <div className="flex w-full md:w-auto">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-none border-0 border-b-2 border-gray-300 dark:border-gray-600 bg-transparent px-4 py-3 text-base text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-slate-900 w-full md:w-72 transition-colors"
                  placeholder={t("newsletter.placeholder")}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="ml-2 px-7 py-6 text-base font-bold tracking-wide focus:outline-none transition-colors disabled:cursor-not-allowed"
                >
                  {loading ? t("newsletter.subscribing") ?? "Subscribing..." : t("newsletter.button")}
                </Button>
              </div>
              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm mt-2 w-full text-right">{error}</div>
              )}
            </form>
            <span className="block text-sm text-gray-500 dark:text-gray-400 mt-3 md:mt-2 md:ml-0">
              {t("newsletter.agreement")}{" "}
              <Link href="/privacy-policy" className="underline hover:text-slate-900 dark:hover:text-white">
                {t("newsletter.privacyLink")}
              </Link>
              .
            </span>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center w-full md:w-auto bg-transparent p-6 animate-fade-in">
            <MailCheck className="h-10 w-10 text-blue-500 dark:text-white mb-2" />
            <div className="text-center">
              <div className="text-green-600 dark:text-green-400 text-lg font-medium mb-2">
                {isExistingSubscriber
                  ? t("newsletter.alreadySubscribed") || "You are already subscribed with this email"
                  : t("newsletter.thankYou") || "Thank you for subscribing!"}
              </div>
              {!isExistingSubscriber && (
                <div className="text-gray-600 dark:text-gray-300 text-sm text-center mb-1">
                  {t("newsletter.successMessage") || "Check your inbox for confirmation."}
                </div>
              )}
            </div>
            <button
              className="text-black dark:text-white text-xs underline hover:text-blue-700 mt-2"
              onClick={resetForm}
            >
              {t("newsletter.resubscribe") || "Subscribe with another email"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}